import React, { useState, useEffect } from 'react';
import apiClient from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, 
  UserPlus, 
  UserMinus, 
  UserX, 
  Check, 
  X, 
  Users, 
  Plus, 
  Trash2, 
  Link as LinkIcon, 
  Copy, 
  LogOut, 
  Shield, 
  Sliders,
  Edit2
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Initials Avatar for Users
const UserAvatar = ({ username, avatarUrl, className = "" }) => {
  if (avatarUrl) {
    return (
      <img 
        src={avatarUrl} 
        alt={username} 
        className={`w-10 h-10 rounded-full object-cover shadow-md shadow-black/10 shrink-0 ${className}`} 
      />
    );
  }
  const initials = username ? username.slice(0, 2).toUpperCase() : "??";
  const colors = [
    "from-red-500 to-orange-500",
    "from-orange-500 to-amber-500",
    "from-green-500 to-emerald-500",
    "from-blue-500 to-indigo-500",
    "from-purple-500 to-pink-500",
    "from-pink-500 to-rose-500"
  ];
  const index = username ? username.charCodeAt(0) % colors.length : 0;
  const gradient = colors[index];
  
  return (
    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold bg-gradient-to-br ${gradient} shadow-md shadow-black/10 shrink-0 ${className}`}>
      <span className="text-xs font-mono tracking-wider">{initials}</span>
    </div>
  );
};

// Initials Avatar for Groups
const GroupAvatar = ({ name, className = "" }) => {
  const initials = name ? name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() : "GP";
  return (
    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold bg-gradient-to-br from-red-600 to-red-900 border border-red-500/20 shadow-md shadow-red-950/20 shrink-0 ${className}`}>
      <span className="text-xs sm:text-sm font-mono tracking-wider">{initials}</span>
    </div>
  );
};

const Friends = () => {
  const { t, language } = useLanguage();
  const { user: currentUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [linkToCopy, setLinkToCopy] = useState('');

  // Group Management
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showManageGroup, setShowManageGroup] = useState(false);

  // Nicknames
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [nicknameTarget, setNicknameTarget] = useState(null);
  const [nicknameValue, setNicknameValue] = useState('');

  const openNicknameModal = (friend) => {
    setNicknameTarget(friend);
    setNicknameValue(friend.nickname || '');
    setShowNicknameModal(true);
  };

  const saveNickname = async () => {
    if (!nicknameTarget) return;
    try {
      await apiClient.post(`/friends/nickname`, {
        friend_id: nicknameTarget.id,
        nickname: nicknameValue.trim() || null
      });
      toast.success(t('nicknameUpdated'));
      setShowNicknameModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update nickname');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [friendsRes, groupsRes] = await Promise.all([
        apiClient.get(`/friends`),
        apiClient.get(`/groups`),
      ]);
      setFriends(friendsRes.data.friends || []);
      setPendingRequests(friendsRes.data.pending_requests || []);
      setGroups(groupsRes.data || []);
      
      // Update selectedGroup if currently open in modal to keep members list in sync
      if (selectedGroup && groupsRes.data) {
        const updatedGroup = groupsRes.data.find(g => g.id === selectedGroup.id);
        if (updatedGroup) {
          setSelectedGroup(updatedGroup);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await apiClient.get(`/users/search?q=${query}`);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const sendFriendRequest = async (userId) => {
    try {
      await apiClient.post(`/friends/request`, { user_id: userId });
      toast.success(t('friendRequestSent'));
      setSearchResults(searchResults.filter(u => u.id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const acceptRequest = async (userId) => {
    try {
      await apiClient.post(`/friends/accept`, { user_id: userId });
      toast.success(t('friendRequestAccepted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const rejectRequest = async (userId) => {
    try {
      await apiClient.post(`/friends/reject`, { user_id: userId });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const removeFriend = async (userId) => {
    try {
      await apiClient.delete(`/friends/${userId}`);
      toast.success(t('friendRemoved'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await apiClient.post(`/groups`, {
        name: newGroupName,
        description: newGroupDesc,
        members: selectedMembers.map(m => m.id),
      });
      toast.success(t('groupCreated'));
      setShowCreateGroup(false);
      setNewGroupName('');
      setNewGroupDesc('');
      setSelectedMembers([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      if (!currentUser) return;
      await apiClient.delete(`/groups/${groupId}/members/${currentUser.id}`);
      toast.success(language === 'de' ? 'Gruppe verlassen' : 'Left group');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave group');
    }
  };

  const kickGroupMember = async (groupId, memberId, memberUsername) => {
    try {
      await apiClient.delete(`/groups/${groupId}/members/${memberId}`);
      toast.success(language === 'de' ? `@${memberUsername} wurde entfernt` : `@${memberUsername} has been removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const copyInviteLink = async (groupId) => {
    let inviteUrl = '';
    try {
      const response = await apiClient.get(`/groups/${groupId}/invite-token`);
      const token = response.data.invite_token;
      inviteUrl = `${window.location.origin}/join-group/${token}`;
    } catch (error) {
      console.error('Failed to get invite token', error);
      toast.error(error.response?.data?.detail || t('failedToCopyInviteLink'));
      return;
    }

    copyToClipboard(inviteUrl, t('inviteLinkCopied'));
  };

  const copyToClipboard = async (text, successMessage) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(successMessage);
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (error) {
      console.warn('Modern clipboard API failed, trying fallback...', error);
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const success = document.execCommand('copy');
        if (success) {
          toast.success(successMessage);
        } else {
          setLinkToCopy(text);
          setShowCopyDialog(true);
        }
      } catch (err) {
        console.error('Fallback copy failed', err);
        setLinkToCopy(text);
        setShowCopyDialog(true);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="app-container" data-testid="friends-page">
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('friends')} & {t('groups')}</h1>

        {/* Social Stats Summary Dashboard */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <Card className="border-border/50 glass">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('friends')}</p>
              <p className="text-xl sm:text-2xl font-bold mt-1 font-mono">{friends.length}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50 glass relative overflow-hidden">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground">{language === 'de' ? 'Anfragen' : 'Requests'}</p>
              <p className={`text-xl sm:text-2xl font-bold mt-1 font-mono ${pendingRequests.length > 0 ? 'text-red-500' : ''}`}>
                {pendingRequests.length}
              </p>
              {pendingRequests.length > 0 && (
                <div className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              )}
            </CardContent>
          </Card>
          <Card className="border-border/50 glass">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-muted-foreground">{language === 'de' ? 'Gruppen' : 'Groups'}</p>
              <p className="text-xl sm:text-2xl font-bold mt-1 font-mono">{groups.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tab-driven layout */}
        <Tabs defaultValue="friends" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="friends" className="text-xs sm:text-sm">
              {t('friends')}
            </TabsTrigger>
            <TabsTrigger value="groups" className="text-xs sm:text-sm">
              {t('groups')}
            </TabsTrigger>
            <TabsTrigger value="requests" className="relative text-xs sm:text-sm">
              {language === 'de' ? 'Anfragen' : 'Requests'}
              {pendingRequests.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.2 text-[9px] font-bold bg-red-500 text-white rounded-full leading-none">
                  {pendingRequests.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Friends Tab */}
          <TabsContent value="friends" className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
            {/* User Search inside Friends Tab */}
            <Card className="border-border/50 bg-card/50">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base flex items-center gap-2 font-semibold">
                  <Search className="w-4 h-4 text-muted-foreground" />
                  {language === 'de' ? 'Neue Freunde finden' : 'Find new friends'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t('searchUsers')}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchUsers(e.target.value);
                    }}
                    className="pl-10 h-10 text-sm"
                    data-testid="user-search-input"
                  />
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-2 mt-2 max-h-60 overflow-y-auto pr-1">
                    {searchResults.map(user => (
                      <div key={user.id} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/20 border border-border/10 gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <UserAvatar username={user.username} avatarUrl={user.avatar_url} className="w-8 h-8" />
                          <div className="min-w-0">
                            <Link to={`/user/${user.id}`} className="font-bold text-sm hover:underline truncate block">
                              {user.nickname || user.username}
                            </Link>
                            {user.nickname && (
                              <span className="text-[10px] text-muted-foreground block -mt-0.5">@{user.username}</span>
                            )}
                            <p className="text-[10px] text-muted-foreground truncate">
                              {user.total_bets} {t('bets')} • {user.win_rate}% {t('winRate')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`font-mono text-xs font-bold ${user.balance < 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {user.balance >= 0 ? '+' : ''}{user.balance.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                          {!friends.find(f => f.id === user.id) && (
                            <Button size="sm" className="h-8 px-3 bg-red-600 hover:bg-red-700 text-xs gap-1.5" onClick={() => sendFriendRequest(user.id)}>
                              <UserPlus className="w-3.5 h-3.5" />
                              <span>{language === 'de' ? 'Hinzufügen' : 'Add'}</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Friends list */}
            <Card className="border-border/50">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base flex items-center justify-between font-semibold">
                  <span>{t('friendsList')}</span>
                  <Badge variant="secondary" className="font-mono text-xs">{friends.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {friends.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {friends.map(friend => (
                      <div key={friend.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/20 border border-border/10 card-hover">
                        <div className="flex items-center gap-3 min-w-0">
                          <UserAvatar username={friend.username} avatarUrl={friend.avatar_url} />
                          <div className="min-w-0">
                            <Link to={`/user/${friend.id}`} className="font-bold text-sm hover:underline truncate block">
                              {friend.nickname || friend.username}
                            </Link>
                            {friend.nickname && (
                              <span className="text-[10px] text-muted-foreground block -mt-0.5">@{friend.username}</span>
                            )}
                            <span className={`font-mono text-xs font-bold block mt-0.5 ${(friend.balance || 0) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                              {(friend.balance || 0) >= 0 ? '+' : ''}{(friend.balance || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8 text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 shrink-0" 
                            onClick={() => openNicknameModal(friend)}
                            title={t('editNickname')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-500/10 shrink-0" 
                            onClick={() => removeFriend(friend.id)}
                            title={t('removeFriend')}
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t('noFriends')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
            <Card className="border-border/50">
              <CardHeader className="flex flex-row items-center justify-between p-4 pb-2">
                <CardTitle className="text-base font-semibold">{t('myGroups')}</CardTitle>
                <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 gap-1.5" onClick={() => setShowCreateGroup(true)}>
                  <Plus className="w-4 h-4" />
                  <span>{t('createGroup')}</span>
                </Button>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {groups.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {groups.map(group => {
                      const isOwner = group.creator_id === currentUser?.id;
                      return (
                        <div key={group.id} className="p-4 rounded-xl bg-secondary/20 border border-border/10 card-hover flex flex-col justify-between h-full space-y-4">
                          <div>
                            <div className="flex items-start gap-3">
                              <GroupAvatar name={group.name} />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <h4 className="font-bold text-sm truncate">{group.name}</h4>
                                  {isOwner && (
                                    <Badge className="bg-yellow-500/10 text-yellow-500 border-none px-1.5 py-0 text-[9px] font-bold gap-0.5">
                                      <Shield className="w-2.5 h-2.5" />
                                      {language === 'de' ? 'Inhaber' : 'Owner'}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                                  {language === 'de' ? 'Erstellt von' : 'Created by'} @{group.creator_username}
                                </p>
                              </div>
                            </div>
                            {group.description && (
                              <p className="text-xs text-muted-foreground mt-3 line-clamp-2 bg-secondary/10 p-2 rounded-lg border border-border/5">{group.description}</p>
                            )}
                            
                            <div className="mt-3">
                              <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-1.5">
                                {t('groupMembers')} ({group.members.length})
                              </p>
                              <div className="flex -space-x-2 overflow-hidden py-0.5">
                                {group.members.slice(0, 5).map(member => (
                                  <div 
                                    key={member.id} 
                                    className="inline-block" 
                                    title={member.username}
                                  >
                                    <UserAvatar username={member.username} avatarUrl={member.avatar_url} className="w-7 h-7 border-2 border-card text-[10px]" />
                                  </div>
                                ))}
                                {group.members.length > 5 && (
                                  <div className="w-7 h-7 rounded-full bg-secondary border-2 border-card flex items-center justify-center text-[10px] font-bold text-muted-foreground font-mono shrink-0">
                                    +{group.members.length - 5}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-2 pt-2 border-t border-border/5">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1 h-8 text-xs gap-1.5 border-blue-500/20 text-blue-400 hover:bg-blue-500/5 hover:text-blue-300" 
                              onClick={() => copyInviteLink(group.id)}
                            >
                              <LinkIcon className="w-3.5 h-3.5" />
                              <span>{language === 'de' ? 'Einladen' : 'Invite'}</span>
                            </Button>
                            
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-xs px-2.5 gap-1.5 border-border text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                              onClick={() => {
                                setSelectedGroup(group);
                                setShowManageGroup(true);
                              }}
                            >
                              <Sliders className="w-3.5 h-3.5" />
                              <span>{language === 'de' ? 'Mitglieder' : 'Members'}</span>
                            </Button>

                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-xs px-2.5 gap-1.5 border-red-500/20 text-red-400 hover:bg-red-500/5 hover:text-red-300"
                              onClick={() => leaveGroup(group.id)}
                              title={t('leaveGroup')}
                            >
                              <LogOut className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t('noGroups')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Friend Requests Tab */}
          <TabsContent value="requests" className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
            <Card className="border-border/50">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base flex items-center justify-between font-semibold">
                  <span>{t('friendRequests')}</span>
                  <Badge variant="secondary" className="font-mono text-xs">{pendingRequests.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {pendingRequests.length > 0 ? (
                  <div className="space-y-2.5">
                    {pendingRequests.map(request => (
                      <div key={request.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/20 border border-border/10 card-hover">
                        <div className="flex items-center gap-3 min-w-0">
                          <UserAvatar username={request.username} avatarUrl={request.avatar_url} />
                          <div className="min-w-0">
                            <span className="font-bold text-sm block truncate">{request.username}</span>
                            <span className="text-[10px] text-muted-foreground block mt-0.5">
                              {language === 'de' ? 'Möchte dein Freund sein' : 'Wants to be friends'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8 border-green-600/30 text-green-500 hover:bg-green-600/10 gap-1.5 px-3" 
                            onClick={() => acceptRequest(request.id)}
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span className="text-xs">{language === 'de' ? 'Annehmen' : 'Accept'}</span>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8 border-red-600/30 text-red-500 hover:bg-red-600/10 gap-1.5 px-3" 
                            onClick={() => rejectRequest(request.id)}
                          >
                            <X className="w-3.5 h-3.5" />
                            <span className="text-xs">{language === 'de' ? 'Ablehnen' : 'Reject'}</span>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <UserPlus className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t('noPendingRequests')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Group Modal */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('createGroup')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('groupName')}</label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Uni Göttingen Wettfreunde"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('groupDescription')}</label>
              <Input
                value={newGroupDesc}
                onChange={(e) => setNewGroupDesc(e.target.value)}
                placeholder={language === 'de' ? 'Beschreibung (optional)' : 'Description (optional)'}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('addMembers')}</label>
              <p className="text-xs text-muted-foreground">
                {language === 'de' ? 'Wähle Freunde aus:' : 'Select friends:'}
              </p>
              <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto p-1.5 border border-border/10 rounded-lg bg-card/30">
                {friends.length > 0 ? friends.map(friend => {
                  const isSelected = selectedMembers.some(m => m.id === friend.id);
                  return (
                    <Badge
                      key={friend.id}
                      variant={isSelected ? 'default' : 'outline'}
                      className="cursor-pointer select-none text-xs px-2 py-0.5 transition-all gap-1"
                      onClick={() => {
                        if (isSelected) {
                          setSelectedMembers(selectedMembers.filter(m => m.id !== friend.id));
                        } else {
                          setSelectedMembers([...selectedMembers, friend]);
                        }
                      }}
                    >
                      <UserAvatar username={friend.username} avatarUrl={friend.avatar_url} className="w-4 h-4 text-[7px]" />
                      {friend.username}
                    </Badge>
                  );
                }) : (
                  <p className="text-xs text-muted-foreground italic p-2">{language === 'de' ? 'Du musst zuerst Freunde hinzufügen' : 'You must add friends first'}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateGroup(false)}>{t('cancel')}</Button>
            <Button onClick={createGroup} disabled={!newGroupName.trim()}>{t('create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage/Details Group Modal */}
      <Dialog open={showManageGroup} onOpenChange={setShowManageGroup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <GroupAvatar name={selectedGroup?.name} className="w-8 h-8 rounded-lg text-xs" />
              <div className="min-w-0 flex-1">
                <span className="block truncate text-base font-bold">{selectedGroup?.name}</span>
                <span className="text-[10px] text-muted-foreground font-normal block mt-0.5">
                  {language === 'de' ? 'Erstellt von' : 'Created by'} @{selectedGroup?.creator_username}
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            {selectedGroup?.description && (
              <div className="bg-secondary/20 p-3 rounded-lg border border-border/10">
                <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-1">
                  {t('groupDescription')}
                </p>
                <p className="text-xs text-foreground leading-relaxed">{selectedGroup.description}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase font-bold tracking-wider text-muted-foreground">
                  {t('groupMembers')} ({selectedGroup?.members.length || 0})
                </label>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-7 text-[10px] font-bold text-blue-400 hover:text-blue-300 p-0 gap-1"
                  onClick={() => {
                    if (selectedGroup) {
                      copyInviteLink(selectedGroup.id);
                    }
                  }}
                >
                  <LinkIcon className="w-3 h-3" />
                  {language === 'de' ? 'Einladungslink kopieren' : 'Copy invite link'}
                </Button>
              </div>
              
              <div className="border border-border/10 rounded-xl divide-y divide-border/10 max-h-60 overflow-y-auto bg-card/30">
                {selectedGroup?.members.map(member => {
                  const isMemberOwner = member.id === selectedGroup.creator_id;
                  const isSelf = member.id === currentUser?.id;
                  const canKick = selectedGroup.creator_id === currentUser?.id && !isMemberOwner;
                  
                  return (
                    <div key={member.id} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <UserAvatar username={member.username} avatarUrl={member.avatar_url} className="w-8 h-8" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Link 
                              to={`/user/${member.id}`} 
                              className="font-bold text-sm hover:underline truncate"
                              onClick={() => setShowManageGroup(false)}
                            >
                              {member.nickname || member.username}
                            </Link>
                            {isSelf && (
                              <Badge className="bg-blue-500/10 text-blue-400 border-none px-1.5 py-0 text-[8px] font-mono leading-none">
                                {language === 'de' ? 'Du' : 'You'}
                              </Badge>
                            )}
                            {isMemberOwner && (
                              <Badge className="bg-yellow-500/10 text-yellow-500 border-none px-1.5 py-0 text-[8px] font-bold gap-0.5 leading-none">
                                <Shield className="w-2 h-2" />
                                {language === 'de' ? 'Inhaber' : 'Owner'}
                              </Badge>
                            )}
                          </div>
                          {member.nickname && (
                            <span className="text-[10px] text-muted-foreground block">@{member.username}</span>
                          )}
                        </div>
                      </div>
                      
                      {canKick && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/5 text-xs gap-1"
                          onClick={() => kickGroupMember(selectedGroup.id, member.id, member.username)}
                        >
                          <UserX className="w-3.5 h-3.5" />
                          <span>{language === 'de' ? 'Entfernen' : 'Remove'}</span>
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="flex sm:justify-between items-center w-full gap-2 mt-2">
            <div>
              {selectedGroup?.creator_id !== currentUser?.id ? (
                <Button 
                  variant="outline" 
                  className="border-red-500/20 text-red-400 hover:bg-red-500/5 hover:text-red-300 text-xs h-9 gap-1.5"
                  onClick={() => {
                    setShowManageGroup(false);
                    leaveGroup(selectedGroup.id);
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t('leaveGroup')}</span>
                </Button>
              ) : (
                <div className="text-[10px] text-muted-foreground italic">
                  {language === 'de' ? 'Du bist der Gruppenersteller' : 'You are the creator'}
                </div>
              )}
            </div>
            <Button variant="secondary" className="h-9 text-xs" onClick={() => setShowManageGroup(false)}>
              {language === 'de' ? 'Schließen' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManualCopyDialog 
        open={showCopyDialog} 
        onOpenChange={setShowCopyDialog} 
        link={linkToCopy} 
        t={t} 
      />

      {/* Edit Nickname Modal */}
      <Dialog open={showNicknameModal} onOpenChange={setShowNicknameModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('setNickname')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('nickname')} ({nicknameTarget?.username})</label>
              <Input
                value={nicknameValue}
                onChange={(e) => setNicknameValue(e.target.value)}
                placeholder={language === 'de' ? 'z.B. Bester Kumpel' : 'e.g. Best Buddy'}
                maxLength={30}
              />
              <p className="text-xs text-muted-foreground">
                {language === 'de' 
                  ? 'Diesen Namen siehst nur du. Leer lassen, um den Standardnamen anzuzeigen.' 
                  : 'Only you can see this name. Leave empty to show the default username.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNicknameModal(false)}>{t('cancel')}</Button>
            <Button onClick={saveNickname}>{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ManualCopyDialog = ({ open, onOpenChange, link, t }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('copyLinkManually')}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center space-x-2 py-4">
          <div className="grid flex-1 gap-2">
            <Input
              readOnly
              value={link}
              className="font-mono text-sm"
            />
          </div>
          <Button
            size="sm"
            className="px-3"
            onClick={() => {
              navigator.clipboard.writeText(link);
              toast.success(t('inviteLinkCopied'));
              onOpenChange(false);
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <DialogFooter className="sm:justify-start">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            {t('close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Friends;
